import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuthInfo, useUser } from '../allauth_auth/hooks.js';
import { useOrg } from '../contexts/OrgContext';

interface TeamGuardProps {
  children: ReactNode;
}

export function TeamGuard({ children }: TeamGuardProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthInfo();
  const user = useUser();
  const { currentOrg, availableOrgs, isLoading, error, switchOrg } = useOrg();

  useEffect(() => {
    const handleTeamRouting = async () => {
      // Still loading teams, wait
      if (isLoading) {
        return;
      }

      // Error loading teams
      if (error) {
        console.error('Team loading error:', error);
        return;
      }

      // No teams available - redirect to team creation or organizations page
      if (availableOrgs.length === 0) {
        if (!user) {
          return;
        }
        navigate(
          user.is_staff === false ? '/organizations' : '/create-organization'
        );
        return;
      }

      // Check if we're on a team-specific route first
      if (orgId) {
        // Validate that orgId is numeric
        if (!/^\d+$/.test(orgId)) {
          // Invalid orgId format - redirect to team selection
          if (availableOrgs.length === 1) {
            navigate(`/${availableOrgs[0].id}`);
          } else {
            navigate('/organizations');
          }
          return;
        }

        const orgIdNum = parseInt(orgId);
        // Verify user has access to this team
        const requestedTeam = availableOrgs.find(t => t.id === orgIdNum);

        if (!requestedTeam) {
          // User doesn't have access to this team
          if (availableOrgs.length === 1) {
            // Redirect to their only available team
            navigate(`/${availableOrgs[0].id}`);
          } else {
            // Multiple teams - show selection screen
            navigate('/organizations');
          }
          return;
        }

        // Switch to the requested team if it's different from current (or if no current team)
        if (!currentOrg || currentOrg.id !== orgIdNum) {
          await switchOrg(requestedTeam.id);
        }
      } else if (!currentOrg) {
        // Not on a team route and no current team selected
        if (availableOrgs.length === 1) {
          // Auto-select the only team and redirect
          await switchOrg(availableOrgs[0].id);
          navigate(`/${availableOrgs[0].id}`);
        } else {
          // Multiple teams - show selection screen
          navigate('/organizations');
        }
        return;
      } else {
        // Legacy route without team ID but has current team - redirect to team-specific route
        const currentPath = location.pathname;
        const dashboardPath = currentPath.replace('/dashboard', '');
        navigate(`/${currentOrg.id}${dashboardPath}`);
      }
    };

    handleTeamRouting();
  }, [
    orgId,
    currentOrg,
    availableOrgs,
    isLoading,
    error,
    navigate,
    location.pathname,
    switchOrg,
    user,
  ]);

  // Show loading state while determining team routing.
  // Only show loading when we don't have a currentOrg yet (initial load).
  // If currentOrg is already set (from a previous successful load), keep
  // rendering children during retries to prevent state loss in child
  // components (e.g., open modals, form data, slideout state).
  //
  // Children are always rendered during the loading window — each page owns
  // its own skeleton. The dashboard page renders DashboardHomeSkeleton when
  // currentOrg is null, so ConversationInput is never unmounted across the
  // loading → content transition.
  if (
    isAuthenticated &&
    !currentOrg &&
    (isLoading || availableOrgs.length > 0)
  ) {
    return children;
  }

  // Show error state
  if (error) {
    return (
      <div className='min-h-full flex items-center justify-center'>
        <div
          role='alert'
          className='flex items-center gap-2 rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-700 max-w-md'
        >
          <span>
            There was an issue loading this page. Try clicking refresh.
          </span>
        </div>
      </div>
    );
  }

  // No teams available
  if (availableOrgs.length === 0) {
    return null; // Will be redirected to create-team
  }

  // No current team selected
  if (!currentOrg) {
    return null; // Will be redirected to team selection or auto-selected
  }

  // Team access verified and current team is set
  return children;
}
